import { useEffect, useState } from 'react';
import TableComponent, { HeadCell } from '../../component/table/table.component';
import { Restaurant } from '../../models';
import { ConditionType, SearchKeyInput, SearchKeyInputItems } from '../../component/form/search-key-input.component';
import apiManage from '../../request';
import PeriodDateFilter from '../../component/period-date-filter.component';
import i18n from '../../i18n';
import { Chip, Grid, Paper, Tooltip, Typography } from '@mui/material';
import { dateFormat } from '../../constants/utils';
import { SharkLoginLogs } from '../../models/shark-login-logs.entity';
import _ from 'lodash';
import { dateValidate, validateCurrentDate } from '../../constants/tool-order';
import moment from 'moment';
import { getResellerFilter } from '../../constants/tool.reseller';

// 时间默认今天
const [START_TIME, END_TIME] = [validateCurrentDate().startTime, validateCurrentDate().endTime];

export default function SharkLoginOperationList() {
    // 表格数据
    const [list, setList] = useState<Restaurant[]>([])
    const [totalCount, setTotalCount] = useState(0)
    // 表格loading
    const [isLoading, setIsLoading] = useState(false)
    // 页码
    const [pagPage, setPagPage] = useState(1)
    const [pagPerPage, setPagPerPage] = useState(20)
    // 排序
    const [sortKey, setSortKey] = useState('')
    const [sortValue, setSortValue] = useState<0 | 1 | 2>(0)
    // 筛选
    const [startDate, setStartDate] = useState(moment(START_TIME, 'YYYY-MM-DD HH:mm').toISOString());
    const [endDate, setEndDate] = useState(moment(END_TIME, 'YYYY-MM-DD HH:mm').toISOString());
    // 时间组件显示操
    const [viewDateTimeData, setViewDateTimeData] = useState<{
        // 开始时间
        startDate: Date | null,
        // 结束时间
        endDate: Date | null,
    }>({
        startDate: moment(START_TIME, 'YYYY-MM-DD HH:mm').toDate(),
        endDate: moment(END_TIME, 'YYYY-MM-DD HH:mm').toDate()
    })
    // 搜索
    const [conditions, setConditions] = useState<ConditionType[]>([]);

    useEffect(() => {
        // 排序、分页改变时重新获取数据
        getData()
    }, [sortKey, sortValue, pagPage, pagPerPage, startDate, endDate, conditions])

    /**
     * 获取数据
     */
    const getData = async () => {
        setIsLoading(true)
        // 搜索条件
        // eslint-disable-next-line prefer-const
        let searchObj = {}
        conditions.forEach((item) => {
            searchObj[item.key] = searchObj[item.key] ? _.concat(searchObj[item.key], item.value) : [item.value];
        })

        // 排序条件
        const sortParams = {
            sort_key: (sortKey === '' || sortValue === 0) ? 'create_date' : sortKey,
            sort_value: (sortValue === 0 || sortValue === 1) ? -1 : 1,
        }
        const result = await apiManage.get_shark_login_logs({
            pagination_page: pagPage,
            pagination_per_page: pagPerPage,
            start_time: startDate,
            end_time: endDate,
            ...sortParams,
            ...searchObj
        })
        // 请求成功
        if (result && !result.error) {
            setList(result?.[0]?.dbResults ?? [])
            setTotalCount(result?.[0]?.totalCount?.[0]?.count ?? 0)
        }
        setIsLoading(false)
    }


    /**
     * 搜索
     * @param conditions
     */
    const handleSearch = (conditions: ConditionType[]) => {
        setPagPage(1)
        setConditions([...conditions])
    }

    /**
     * 排序
     * @param headCell
     */
    const handleSort = (headCell: HeadCell) => {
        let newSortValue = sortValue;
        switch (headCell.sortStatus) {
            case 0: {
                newSortValue = 1;
                break;
            }
            case 1: {
                newSortValue = 2;
                break;
            }
            case 2: {
                newSortValue = 0;
                break;
            }
        }
        setPagPage(1)
        setSortKey(headCell.id)
        setSortValue(newSortValue)
    }

    /**
     * 页码回调
     * @param params
     */
    const changePageCallback = (params: {
        page: number;
        curRowsPerPage: number;
    }) => {
        setPagPerPage(params.curRowsPerPage)
        setPagPage(params.page + 1)
    }

    /**
     * 周期/自定义日期范围变更（与 PeriodDateFilter 对接）
     * @param dates - 选择器当前起止时间
     */
    const handlePeriodDateChange = (dates: [Date | null, Date | null]) => {
        setViewDateTimeData({
            startDate: dates[0],
            endDate: dates[1],
        });
        if (dates[0] && dates[1] && dateValidate(dates[0], dates[1])) {
            setStartDate(moment(dates[0]).toISOString());
            setEndDate(moment(dates[1]).toISOString());
            setPagPage(1);
        }
    };

    // 表格头部
    const tableHeadCell: HeadCell[] = [
        {
            id: 'staff_name',
            label: i18n.t('business_analysis_page_text_staff_name'),
            sortStatus: sortKey === 'staff_name' ? sortValue : 0,
            renderFunction: (row: SharkLoginLogs) => {
                return `${row?.staff_id ?? ''}/${row?.staff_name ?? ''}`
            }
        },
        {
            id: 'nickname',
            label: i18n.t('restaurant_name'),
            sortStatus: sortKey === 'nickname' ? sortValue : 0,
        },
        {
            id: 'group_name',
            label: i18n.t('group_management_page_text_group_name'),
            sortStatus: sortKey === 'group_name' ? sortValue : 0,
        },
        {
            id: 'project',
            label: i18n.t('shark_log_page_text_project'),
            sortStatus: sortKey === 'project' ? sortValue : 0,
        },
        {
            id: 'device_number',
            label: i18n.t('shark_log_page_text_device_number'),
            sortStatus: sortKey === 'device_number' ? sortValue : 0,
        },
        {
            id: 'device_type',
            label: i18n.t('shark_log_page_text_device_type'),
            sortStatus: sortKey === 'device_type' ? sortValue : 0,
        },
        {
            id: 'subitems_version',
            label: i18n.t('shark_log_page_text_subitems_version'),
            sortStatus: sortKey === 'subitems_version' ? sortValue : 0,
        },
        {
            id: 'shark_version',
            label: i18n.t('shark_log_page_text_shark_version'),
            sortStatus: sortKey === 'shark_version' ? sortValue : 0,
        },
        {
            id: 'subitems_ip',
            label: i18n.t('shark_log_page_text_subitems_ip'),
            sortStatus: sortKey === 'subitems_ip' ? sortValue : 0,
        },
        {
            id: 'ip',
            label: i18n.t('shark_log_page_text_shark_ip'),
            sortStatus: sortKey === 'ip' ? sortValue : 0,
        },
        {
            id: 'create_date',
            label: i18n.t('operation_record_operation_time'),
            sortStatus: sortKey === 'create_date' ? sortValue : 0,
            renderFunction: (row: SharkLoginLogs) => {
                return dateFormat(row.create_date, 'YYYY/MM/DD HH:mm:ss')
            }
        },
        {
            id: 'login_status',
            label: i18n.t('deliverect_log_page_text_log_status'),
            sortStatus: sortKey === 'login_status' ? sortValue : 0,
            renderFunction: (row: SharkLoginLogs) => {
                const failInfo = typeof row.detail !== 'string' ? JSON.stringify(row.detail) : row.detail
                return (
                    <Chip
                        size='small'
                        label={(
                            <Tooltip title={failInfo} >
                                <Typography sx={{ color: '#fff', fontSize: '13px' }}>{row.status ? i18n.t('general_text_success') : i18n.t('general_text_failed')}</Typography>
                            </Tooltip>)}
                        color={row.status ? 'success' : 'error'}
                    />
                )
            }
        }
    ]

    // 子项目下拉列表
    const projectOptions = ['RWA2', 'COP2', 'CWA2', 'MOP2', 'KWA2', 'RBP2', 'QOP2','Ecco POS'].map((item) => ({
        value: item,
        label: item
    }))

    // 查找表格条件下拉框 静态数据
    let filterList: SearchKeyInputItems[] = [
        {
            value: 'staff_name',
            label: i18n.t('business_analysis_page_text_staff_name'),
            type: 'TEXT'
        },
        {
            value: 'nickname',
            label: i18n.t('restaurant_name'),
            type: 'TEXT'
        },
        {
            value: 'group_name',
            label: i18n.t('operation_record_group_nickname'),
            type: 'TEXT'
        },
        {
            value: 'project',
            label: i18n.t('shark_log_page_text_project'),
            type: 'SELECT',
            options: projectOptions
        },
        {
            value: 'device_number',
            label: i18n.t('shark_log_page_text_device_number'),
            type: 'TEXT'
        },
        {
            value: 'device_type',
            label: i18n.t('shark_log_page_text_device_type'),
            type: 'TEXT'
        },
        {
            value: 'subitems_version',
            label: i18n.t('shark_log_page_text_subitems_version'),
            type: 'TEXT'
        },
        {
            value: 'shark_version',
            label: i18n.t('shark_log_page_text_shark_version'),
            type: 'TEXT'
        },
        {
            value: 'subitems_ip',
            label: i18n.t('shark_log_page_text_subitems_ip'),
            type: 'TEXT'
        },
        {
            value: 'ip',
            label: i18n.t('shark_log_page_text_shark_ip'),
            type: 'TEXT'
        },
    ];

    filterList = getResellerFilter(filterList);


    return (
        <Paper sx={{ padding: 4 }}>
            <Grid sx={{ mb: 3 }} container justifyContent='space-between' alignItems='center'>
                <SearchKeyInput
                    items={filterList}
                    conditions={conditions}
                    onSearch={handleSearch}
                    showConditionsBox
                />
                <PeriodDateFilter
                    startDate={startDate}
                    endDate={endDate}
                    pickerValue={[viewDateTimeData.startDate, viewDateTimeData.endDate]}
                    onDateTimeChange={handlePeriodDateChange}
                />
            </Grid>
            <TableComponent
                {...{
                    showChecked: 0,
                    list,
                    isUseCustomPagination: true,
                    headCellList: tableHeadCell,
                    isTableLoading: isLoading,
                    handleSort,
                    isPagination: true,
                    allCount: totalCount,
                    changePageCallback,
                    isCurrentPageData: true,
                    currentPage: pagPage - 1
                }}
            />
        </Paper>
    )
}